/**
 * pages/hazard/VerifyFix.js — ӨӨРӨӨ ШИЙДСЭНИЙГ БАТАЛГААЖУУЛАХ + ҮЗҮҮЛЭЛТ.
 *
 * Route: /hazard/verify
 *
 * Tab-ууд:
 *   • Хүлээгдэж буй — ажилтны «өөрөө шийдсэн» бүртгэлийг удирдлага уншиж
 *                     БАТЛАНА (хаагдана) эсвэл ШАЛТГААНТАЙ буцаана.
 *   • Үзүүлэлт      — ажилтан бүрээр шийдсэн / баталгаажсан / буцаагдсан
 *                     тоо ба батлагдалтын хувь (урамшууллын эх өгөгдөл).
 *
 * ⚠ ХЭН БАТАЛГААЖУУЛАХ ВЭ: удирдлагын аль ч түвшин (SSO бүлэг 166/167/168)
 *   эсвэл ХАБЭА хэлтэс. АШУА-гийн АЛБАН жагсаалт баазад байхгүй тул албан
 *   тушаалын бүлгээс өөр найдвартай эх сурвалж алга (ashua_daily.py-д мөн
 *   ийн тэмдэглэсэн).
 *
 * ⚠ ӨӨРИЙН ШИЙДСЭНИЙГ ӨӨРӨӨ БАТАЛГААЖУУЛАХГҮЙ — серверт ч хориглосон.
 *   Эс бөгөөс урамшууллын үзүүлэлт өөрөө өөрийгөө баталсан мөрөөр дүүрнэ.
 *
 * ⚠ БАТЛАГДАЛТЫН ХУВЬ нь ШИЙДВЭРЛЭГДСЭН дээр бодогдоно
 *   (батлагдсан / (батлагдсан + буцаагдсан)). Хүлээгдэж буйг хуваарьт
 *   оруулбал удирдлага удаашрахад ажилтны үзүүлэлт унана — ажилтны
 *   хяналтад байдаггүй зүйлээр нь дүгнэх нь шударга бус.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Empty,
  Image,
  Input,
  Modal,
  Radio,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  BarChartOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  EyeOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";

import {
  getVerifyQueue,
  updateVerifyNote,
  verifyHazardFix,
} from "src/api/hazard";
import { xabRewardApi } from "src/api/xabApi";
import { SEV_COLOR } from "src/components/hazard";
import {
  DateRangeFilter,
  SearchInput,
  UnitFilter,
  currentMonthRange,
} from "src/components/filters";
import PageHelp from "src/components/help/PageHelp";

const PAGE_BG = "#F1F3F6";
const { TextArea } = Input;

/* ────────────────── Шийдвэрийн цонх (батлах / буцаах) ─────────────────── */
/**
 * Нэг цонх ГУРВАН зориулалттай:
 *   mode="approve" — батлахад САНАЛ бичнэ (заавал биш)
 *   mode="reject"  — буцаахад ШАЛТГААН бичнэ (ЗААВАЛ — ажилтан үүнийг харна)
 *   mode="edit"    — шийдвэрлэсний дараа саналаа засна (шийдвэр хөндөгдөхгүй)
 */
function DecisionModal({ row, mode, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Засах горимд одоо байгаа саналыг урьдчилан ачаална
    setNote(mode === "edit" ? row?.self_fix?.verify_note || "" : "");
  }, [row?.id, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const cfg = {
    approve: {
      title: "Баталгаажуулах",
      ok: "Батлах",
      danger: false,
      placeholder:
        "Санал / тэмдэглэл (заавал биш) — ж: чанартай гүйцэтгэсэн, дахин давтагдахаас сэргийлэх арга хэмжээ авсан",
      hint: "Батласнаар бүртгэл ХААГДАЖ, ажилтны урамшууллын үзүүлэлтэд тоологдоно.",
    },
    reject: {
      title: "Буцаах шалтгаан",
      ok: "Буцаах",
      danger: true,
      placeholder: "Юу дутуу байна вэ, ажилтан юуг дахин хийх ёстой вэ",
      hint: "Бүртгэл «Хянагдаж буй» төлөвт буцна — ажилтан засаад дахин илгээж болно.",
    },
    edit: {
      title: "Санал засах",
      ok: "Хадгалах",
      danger: false,
      placeholder: "Саналаа засварлана",
      hint: "ЗӨВХӨН тайлбар засагдана — шийдвэр (батлагдсан/буцаагдсан) хэвээр үлдэнэ. Засвар нь түүхэнд бичигдэнэ.",
    },
  }[mode || "approve"];

  const submit = async () => {
    const text = note.trim();
    if (mode === "reject" && !text)
      return message.warning("Буцаах шалтгаанаа бичнэ үү — ажилтан үүнийг харна.");
    setBusy(true);
    try {
      if (mode === "edit") {
        await updateVerifyNote(row.id, text);
        message.success("Саналыг шинэчиллээ.");
      } else {
        await verifyHazardFix(row.id, mode === "approve", text || undefined);
        message.success(mode === "approve" ? "Баталгаажууллаа." : "Буцаалаа.");
      }
      onDone();
      onClose();
    } catch (ex) {
      message.error(ex?.response?.data?.message || "Хадгалж чадсангүй.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={!!row}
      title={cfg.title}
      onCancel={onClose}
      onOk={submit}
      okText={cfg.ok}
      okButtonProps={{ danger: cfg.danger }}
      cancelText="Болих"
      confirmLoading={busy}
    >
      <div style={{ fontSize: 12, color: "#8A94A6", marginBottom: 6 }}>
        {row?.report_number} · шийдсэн: {row?.self_fix?.by_name || "—"}
      </div>
      <div
        style={{
          background: "#F8FAFC",
          border: "1px solid #E6E9EF",
          borderRadius: 6,
          padding: "6px 8px",
          fontSize: 12,
          marginBottom: 8,
        }}
      >
        {row?.self_fix?.action || "—"}
      </div>
      <TextArea
        rows={4}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={cfg.placeholder}
      />
      <div style={{ fontSize: 10.5, color: "#9AA3B0", marginTop: 4 }}>
        {cfg.hint}
      </div>
    </Modal>
  );
}

/* ───────────────────────── Дэлгэрэнгүй танилцах ───────────────────────── */
/**
 * Шийдвэр гаргахын өмнө БҮХ мэдээллийг нэг дор.
 *
 * ⚠ Өмнө нь хүснэгтийн мөрөнд багтсан хэсгээр л шийдвэр гаргах шаардлагатай
 *   байсан — тайлбар таслагдаж, зураг огт харагддаггүй байв. Зураг бол
 *   «үнэхээр зассан уу» гэдгийн цорын ганц нотолгоо.
 */
function DetailModal({ row, onClose, onApprove, onReject, onEditNote, onOpenFull }) {
  if (!row) return null;
  const sf = row.self_fix || {};
  const before = (row.photos || []).filter((p) => p.is_before);
  const after = (row.photos || []).filter((p) => !p.is_before);
  const decided = sf.verify_status === "APPROVED" || sf.verify_status === "REJECTED";

  const Row = ({ label, children }) =>
    children ? (
      <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
        <span style={{ width: 120, flexShrink: 0, fontSize: 11.5, color: "#8A94A6" }}>
          {label}
        </span>
        <span style={{ fontSize: 12.5, minWidth: 0 }}>{children}</span>
      </div>
    ) : null;

  const Photos = ({ list, title }) =>
    list.length ? (
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, marginBottom: 4 }}>
          {title} ({list.length})
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {list.map((p) => (
            <Image
              key={p.id}
              src={p.file_url}
              width={92}
              height={92}
              style={{ objectFit: "cover", borderRadius: 6 }}
            />
          ))}
        </div>
      </div>
    ) : null;

  return (
    <Modal
      open={!!row}
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontFamily: "monospace" }}>{row.report_number}</span>
          <Tag
            style={{
              marginRight: 0,
              color: "#fff",
              background: SEV_COLOR[row.severity] || "#8A94A6",
              border: "none",
            }}
          >
            {row.severity_label || row.severity}
          </Tag>
          {decided ? (
            <Tag color={sf.verify_status === "APPROVED" ? "green" : "red"}>
              {sf.verify_status === "APPROVED" ? "Баталгаажсан" : "Буцаагдсан"}
            </Tag>
          ) : (
            <Tag color="processing">Хүлээгдэж буй</Tag>
          )}
        </span>
      }
      onCancel={onClose}
      width={680}
      footer={
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button onClick={onOpenFull}>Бүтэн хуудас</Button>
          {row.can_edit_note ? (
            <Button icon={<EditOutlined />} onClick={onEditNote}>
              Санал засах
            </Button>
          ) : null}
          {row.can_verify ? (
            <>
              <Button danger icon={<CloseOutlined />} onClick={onReject}>
                Буцаах
              </Button>
              <Button type="primary" icon={<CheckOutlined />} onClick={onApprove}>
                Батлах
              </Button>
            </>
          ) : null}
          <Button onClick={onClose}>Хаах</Button>
        </div>
      }
    >
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Аюул</div>
      <Row label="Тайлбар">{row.description}</Row>
      <Row label="Ангилал">{row.hazard_category_name}</Row>
      <Row label="Байршил">{row.location_text}</Row>
      <Row label="Нэгж">{row.insert_bn_name || row.insert_dept_name}</Row>
      <Row label="Мэдээлсэн">
        {row.is_anonymous ? "Нэрээ нууцалсан" : row.insert_user_name}
      </Row>
      <Row label="Бүртгэсэн">{(row.report_date || "").slice(0, 10)}</Row>
      <Photos list={before} title="Илэрсэн үеийн зураг" />

      <div style={{ fontWeight: 700, fontSize: 13, margin: "14px 0 6px" }}>
        Авсан арга хэмжээ
      </div>
      <div
        style={{
          background: "#F6FFED",
          border: "1px solid #B7EB8F",
          borderRadius: 6,
          padding: "8px 10px",
          fontSize: 12.5,
          whiteSpace: "pre-wrap",
        }}
      >
        {sf.action || "—"}
      </div>
      <div style={{ marginTop: 6 }}>
        <Row label="Шийдсэн">
          {sf.by_name || sf.by_tn} · {(sf.at || "").slice(0, 16).replace("T", " ")}
        </Row>
      </div>
      <Photos list={after} title="Зассаны дараах зураг" />

      {decided ? (
        <>
          <div style={{ fontWeight: 700, fontSize: 13, margin: "14px 0 6px" }}>
            Баталгаажуулалт
          </div>
          <Row label="Шийдвэр">
            {sf.verify_status === "APPROVED" ? "Баталсан" : "Буцаасан"}
          </Row>
          <Row label="Хэн">{sf.verified_by_name || sf.verified_by_tn}</Row>
          <Row label="Хэзээ">
            {(sf.verified_at || "").slice(0, 16).replace("T", " ")}
          </Row>
          <Row label="Санал">{sf.verify_note}</Row>
          {!sf.verify_note ? (
            <div style={{ fontSize: 11, color: "#B0B7C3" }}>Санал бичээгүй.</div>
          ) : null}
        </>
      ) : null}
    </Modal>
  );
}

/* ──────────────────────── Таб 1 — Хүлээгдэж буй ───────────────────────── */
function PendingTab() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  // Дэлгэрэнгүй цонх ба шийдвэрийн цонх (approve | reject | edit)
  const [detail, setDetail] = useState(null);
  const [decision, setDecision] = useState({ row: null, mode: null });
  // Аль төлөвийн жагсаалт: хүлээгдэж буй / баталгаажсан / буцаагдсан.
  // ⚠ Баталгаажсаныг ЗӨВХӨН тоогоор («Үзүүлэлт» таб) харуулдаг байсан тул
  //   «аль бүртгэл батлагдсан бэ» гэдгийг хаанаас ч харах боломжгүй байв.
  const [state, setState] = useState("pending");

  const load = useCallback(() => {
    setLoading(true);
    getVerifyQueue({ page, per_page: pageSize, state })
      .then(setData)
      .catch((e) =>
        message.error(e?.response?.data?.message || "Жагсаалт татаж чадсангүй"),
      )
      .finally(() => setLoading(false));
  }, [page, pageSize, state]);

  useEffect(() => {
    load();
  }, [load]);

  // Цонхнуудыг нээх — дэлгэрэнгүйгээс шууд шийдвэрт шилжинэ
  const openDecision = (row, mode) => {
    setDetail(null);
    setDecision({ row, mode });
  };
  const closeDecision = () => setDecision({ row: null, mode: null });

  const columns = [
    {
      title: "Дугаар",
      dataIndex: "report_number",
      width: 150,
      render: (v, r) => (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, fontFamily: "monospace", fontSize: 11.5 }}
          // ⚠ Дугаар дарахад ДЭЛГЭРЭНГҮЙ ЦОНХ нээгдэнэ (хуудас солихгүй) —
          //   шийдвэр гаргаад жагсаалтдаа үлдэх нь ажлын урсгалд илүү.
          //   Бүтэн хуудас руу цонхны доод талын товчоор орно.
          onClick={() => setDetail(r)}
        >
          {v}
        </Button>
      ),
    },
    {
      title: "Түвшин",
      dataIndex: "severity",
      width: 92,
      align: "center",
      render: (v, r) => (
        <Tag
          style={{
            marginRight: 0,
            color: "#fff",
            background: SEV_COLOR[v] || "#8A94A6",
            border: "none",
          }}
        >
          {r.severity_label || v}
        </Tag>
      ),
    },
    {
      title: "Аюул",
      dataIndex: "description",
      render: (v, r) => (
        <div>
          <div style={{ fontSize: 12.5 }}>{v || "—"}</div>
          <div style={{ fontSize: 11, color: "#9AA3B0" }}>
            {r.hazard_category_name || "ангилалгүй"}
            {r.location_text ? " · " + r.location_text : ""}
          </div>
        </div>
      ),
    },
    {
      title: "Авсан арга хэмжээ",
      dataIndex: ["self_fix", "action"],
      render: (v) => <div style={{ fontSize: 12.5 }}>{v || "—"}</div>,
    },
    {
      title: "Шийдсэн",
      dataIndex: ["self_fix", "by_name"],
      width: 150,
      render: (v, r) => (
        <div style={{ fontSize: 11.5 }}>
          {v || "—"}
          <div style={{ color: "#9AA3B0", fontSize: 10.5 }}>
            {(r.self_fix?.at || "").slice(0, 10)}
          </div>
        </div>
      ),
    },
    {
      title: state === "pending" ? "Шийдвэр" : "Баталгаажуулалт",
      width: 150,
      align: "center",
      render: (_v, r) => {
        // ── Шийдвэрлэгдсэн мөр — ХЭН, ХЭЗЭЭ шийдсэн нь харагдана ──
        if (state !== "pending") {
          const sf = r.self_fix || {};
          const ok = sf.verify_status === "APPROVED";
          return (
            <div style={{ lineHeight: 1.3 }}>
              <Tag color={ok ? "green" : "red"} style={{ marginRight: 0 }}>
                {ok ? "Баталгаажсан" : "Буцаагдсан"}
              </Tag>
              <div style={{ fontSize: 10.5, color: "#8A94A6", marginTop: 2 }}>
                {sf.verified_by_name || sf.verified_by_tn || "—"}
              </div>
              <div style={{ fontSize: 10, color: "#B0B7C3" }}>
                {(sf.verified_at || "").slice(0, 10)}
              </div>
              {!ok && sf.verify_note ? (
                <Tooltip title={sf.verify_note}>
                  <div
                    style={{
                      fontSize: 10,
                      color: "#DC2626",
                      maxWidth: 130,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {sf.verify_note}
                  </div>
                </Tooltip>
              ) : null}
            </div>
          );
        }
        return r.can_verify ? (
          <Space size={4}>
            <Button
              size="small"
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => openDecision(r, "approve")}
            >
              Батлах
            </Button>
            <Button
              size="small"
              danger
              icon={<CloseOutlined />}
              onClick={() => openDecision(r, "reject")}
            />
          </Space>
        ) : (
          <Tooltip
            title={
              data?.can_verify
                ? "Өөрийн шийдсэн бүртгэлийг өөрөө баталгаажуулах боломжгүй"
                : "Танд баталгаажуулах эрх байхгүй"
            }
          >
            <Tag color="processing" style={{ marginRight: 0 }}>
              хүлээгдэж буй
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      // ⚠ БҮХ гурван төлөвт байна — шийдвэр гаргахаас өмнө (эсвэл гаргасны
      //   дараа шалгахад) бүх мэдээлэл, ЗУРАГТАЙГАА нэг дор харагдах ёстой.
      title: "",
      width: 110,
      align: "center",
      render: (_v, r) => (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => setDetail(r)}>
            Дэлгэрэнгүй
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
          flexWrap: "wrap",
        }}
      >
        {/* Төлөвийн шилжүүлэгч — «аль нь батлагдсан бэ» гэдгийг эндээс харна */}
        <Radio.Group
          value={state}
          onChange={(e) => {
            setPage(1);
            setState(e.target.value);
          }}
          optionType="button"
          buttonStyle="solid"
          size="small"
        >
          <Radio.Button value="pending">Хүлээгдэж буй</Radio.Button>
          <Radio.Button value="approved">Баталгаажсан</Radio.Button>
          <Radio.Button value="rejected">Буцаагдсан</Radio.Button>
        </Radio.Group>
        <span style={{ fontSize: 12, color: "#8A94A6" }}>
          {state !== "pending"
            ? "Шийдвэрлэгдсэн бүртгэлийн түүх — хэн шийдсэн, хэн баталсан, хэзээ."
            : data?.can_verify
              ? "Ажилтны шийдсэнийг уншиж баталгаажуулна. Батлагдсан бүртгэл хаагдаж, урамшууллын үзүүлэлтэд тоологдоно."
              : "Танд баталгаажуулах эрх байхгүй тул зөвхөн ӨӨРИЙН илгээсэн бүртгэл харагдана."}
        </span>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={load}
          style={{ marginLeft: "auto" }}
        >
          Шинэчлэх
        </Button>
      </div>

      {data && data.columns_ready === false ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 10 }}
          message="Бааз бэлэн болоогүй — self_fix_* / verify_* багана нэмэгдээгүй байна"
        />
      ) : null}

      <Card size="small" bodyStyle={{ padding: 0 }}>
        <Table
          size="small"
          rowKey="id"
          loading={loading}
          dataSource={data?.items || []}
          columns={columns}
          locale={{
            emptyText: (
              <Empty
                description={
                  state === "approved"
                    ? "Баталгаажсан бүртгэл алга"
                    : state === "rejected"
                      ? "Буцаагдсан бүртгэл алга"
                      : "Баталгаажуулах бүртгэл алга"
                }
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total || 0,
            size: "small",
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>

      <DetailModal
        row={detail}
        onClose={() => setDetail(null)}
        onApprove={() => openDecision(detail, "approve")}
        onReject={() => openDecision(detail, "reject")}
        onEditNote={() => openDecision(detail, "edit")}
        onOpenFull={() =>
          navigate("/hazard/" + detail.id, { state: { from: "/hazard/verify" } })
        }
      />

      <DecisionModal
        row={decision.row}
        mode={decision.mode}
        onClose={closeDecision}
        onDone={load}
      />
    </div>
  );
}

/* ─────────────────────────── Таб 2 — Үзүүлэлт ─────────────────────────── */
function KpiTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => currentMonthRange().from);
  const [dateTo, setDateTo] = useState(() => currentMonthRange().to);
  const [deptCode, setDeptCode] = useState("");
  const [deptParentId, setDeptParentId] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    xabRewardApi
      .selfFixSummary({
        from: dateFrom || undefined,
        to: dateTo || undefined,
        bn_code: deptCode || undefined,
        q: q || undefined,
        limit: 500,
      })
      .then(setData)
      .catch((e) =>
        message.error(e?.response?.data?.message || "Үзүүлэлт татаж чадсангүй"),
      )
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, deptCode, q]);

  useEffect(() => {
    load();
  }, [load]);

  const s = data?.summary || {};
  const workers = useMemo(() => data?.workers || [], [data]);

  const columns = [
    {
      title: "Ажилтан",
      dataIndex: "name",
      render: (v, r) => (
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 600 }}>
            {v || "ТН " + r.tn}
            {!r.known ? (
              <Tooltip title="Ажилтны лавлахаас олдсонгүй — ТН зөрүүтэй байж болно">
                <Tag style={{ marginLeft: 6, fontSize: 10 }}>тодорхойгүй</Tag>
              </Tooltip>
            ) : null}
          </div>
          <div style={{ fontSize: 10.5, color: "#9AA3B0" }}>
            {r.position || "—"}
            {r.bn_name ? " · " + r.bn_name : ""}
          </div>
        </div>
      ),
    },
    {
      title: "Шийдсэн",
      dataIndex: "total",
      width: 90,
      align: "center",
      sorter: (a, b) => a.total - b.total,
    },
    {
      title: "Баталгаажсан",
      dataIndex: "approved",
      width: 120,
      align: "center",
      sorter: (a, b) => a.approved - b.approved,
      render: (v) => (
        <b style={{ color: v ? "#16A34A" : "#C4C9D1" }}>{v}</b>
      ),
    },
    {
      title: "Буцаагдсан",
      dataIndex: "rejected",
      width: 110,
      align: "center",
      render: (v) => (v ? <span style={{ color: "#DC2626" }}>{v}</span> : "—"),
    },
    {
      title: "Хүлээгдэж буй",
      dataIndex: "pending",
      width: 120,
      align: "center",
      render: (v) => (v ? <Tag color="processing">{v}</Tag> : "—"),
    },
    {
      title: "Батлагдалт",
      dataIndex: "approve_rate",
      width: 110,
      align: "center",
      sorter: (a, b) => (a.approve_rate || 0) - (b.approve_rate || 0),
      render: (v) =>
        v === null || v === undefined ? (
          // Шийдвэрлэгдсэн бүртгэлгүй үед хувь ҮГҮЙ — 0% гэж бичвэл ажилтан
          // муу ажилласан мэт харагдана.
          <Tooltip title="Шийдвэрлэгдсэн бүртгэл алга">
            <span style={{ color: "#C4C9D1" }}>—</span>
          </Tooltip>
        ) : (
          <b style={{ color: v >= 80 ? "#16A34A" : v >= 50 ? "#CA8A04" : "#DC2626" }}>
            {v}%
          </b>
        ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "flex-end",
          flexWrap: "wrap",
          marginBottom: 10,
        }}
      >
        <DateRangeFilter
          from={dateFrom}
          to={dateTo}
          onFromChange={setDateFrom}
          onToChange={setDateTo}
        />
        {/* ⚠ UnitFilter нь цех + хэсэг ХОЁР элемент буцаадаг ба тус бүр
            `flex: 1 1 170px`. Ороосон блок нь flex биш бол доош давхарлана. */}
        <div style={{ display: "flex", gap: 8, minWidth: 330 }}>
          <UnitFilter
            deptCode={deptCode}
            deptParentId={deptParentId}
            onDeptChange={(code, parentId) => {
              setDeptCode(code || "");
              setDeptParentId(parentId ?? null);
            }}
          />
        </div>
        <SearchInput
          value={q}
          size="small"
          debounce={400}
          onChange={setQ}
          placeholder="Нэр, ТН, албан тушаалаар"
          style={{ width: 240, flex: "0 0 auto" }}
        />
        <Button size="small" icon={<ReloadOutlined />} onClick={load}>
          Шинэчлэх
        </Button>
      </div>

      {data?.unavailable ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 10 }}
          message="Үзүүлэлт тооцох боломжгүй"
          description={data.message}
        />
      ) : null}

      {/* Хураангуй */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <Stat label="Оролцсон ажилтан" value={s.worker_count || 0} color="#0F6E56" />
        <Stat label="Нийт шийдсэн" value={s.total || 0} color="#1677FF" />
        <Stat label="Баталгаажсан" value={s.approved || 0} color="#16A34A" />
        <Stat label="Буцаагдсан" value={s.rejected || 0} color="#DC2626" />
        <Stat label="Хүлээгдэж буй" value={s.pending || 0} color="#CA8A04" />
        <Stat
          label="Батлагдалт"
          value={s.approve_rate == null ? "—" : s.approve_rate + "%"}
          color="#531DAB"
        />
      </div>

      {/* Туршилтын данс хасагдсаныг чимээгүй өнгөрөөхгүй */}
      {s.excluded_tn_count ? (
        <div style={{ fontSize: 11, color: "#9AA3B0", marginBottom: 8 }}>
          <Tag style={{ marginRight: 6 }}>{s.excluded_tn_count} ТН хасагдсан</Tag>
          Туршилтын данс ба хүчингүй ТН тооцоонд ороогүй.
        </div>
      ) : null}

      <Card size="small" bodyStyle={{ padding: 0 }}>
        <Table
          size="small"
          rowKey="tn"
          loading={loading}
          dataSource={workers}
          columns={columns}
          locale={{ emptyText: <Empty description="Энэ хугацаанд бүртгэл алга" /> }}
          pagination={{ pageSize: 25, size: "small", showSizeChanger: true }}
        />
      </Card>

      <div style={{ marginTop: 8, fontSize: 11, color: "#9AA3B0" }}>
        Урамшууллын тооцоонд (<b>/reward/system</b> → «Өөрөө шийдсэн») ЗӨВХӨН
        баталгаажсан тоо ордог. Тэнд «Аюул арилгасан» баганаас өөрөө шийдсэн
        бүртгэл хасагдсан тул давхар тоологдохгүй.
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #E6E9EF",
        borderRadius: 10,
        padding: "8px 12px",
        minWidth: 120,
        flex: "1 1 120px",
      }}
    >
      <div style={{ fontSize: 19, fontWeight: 800, color, lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, marginTop: 2 }}>{label}</div>
    </div>
  );
}

/* ──────────────────────────── Үндсэн хуудас ───────────────────────────── */
export default function VerifyFix() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("PENDING");

  return (
    <div style={{ background: PAGE_BG, minHeight: "100%", padding: 16 }}>
      <div
        style={{
          background: "#fff",
          border: "1px solid #E6E9EF",
          borderRadius: 12,
          padding: "14px 16px",
          marginBottom: 12,
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <Button
          icon={<ArrowLeftOutlined />}
          size="small"
          onClick={() => navigate(-1)}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1
            className="text-xl font-bold text-gray-900 m-0"
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <SafetyCertificateOutlined style={{ color: "#0F6E56" }} />
            Өөрөө шийдсэн — баталгаажуулалт
            <PageHelp topic="hazard/verify" />
          </h1>
          <div className="text-sm text-gray-500 mt-1">
            Ажилтны өөрөө арилгасан аюулыг удирдлага баталгаажуулж, хаана —
            баталгаажсан тоо нь урамшууллын үзүүлэлт болно
          </div>
        </div>
        <Button onClick={() => navigate("/hazard/board")}>Нээлттэй самбар</Button>
      </div>

      <Card bodyStyle={{ padding: 0 }}>
        <Tabs
          activeKey={tab}
          onChange={setTab}
          tabBarStyle={{ paddingLeft: 16, paddingRight: 16, marginBottom: 0 }}
          items={[
            {
              key: "PENDING",
              label: (
                <Space size={6}>
                  <CheckOutlined style={{ color: "#0F6E56" }} />
                  <span>Хүлээгдэж буй</span>
                </Space>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  <PendingTab />
                </div>
              ),
            },
            {
              key: "KPI",
              label: (
                <Space size={6}>
                  <BarChartOutlined style={{ color: "#531DAB" }} />
                  <span>Үзүүлэлт</span>
                </Space>
              ),
              children: (
                <div style={{ padding: 16 }}>
                  <KpiTab />
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
